import dayjs from 'dayjs'
import { useState } from 'react'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/macro'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Stack, Alert, Divider, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { ShiftTemplate } from '@/core/models/shift.model'
import { EligibleStaffResponse } from '@/core/models/planning.model'
import { StaffSelector } from './StaffSelector'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

interface ShiftTemplateDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (staffIds: string[]) => void
  onDeleteClick?: () => void
  onEditClick?: () => void
  shiftTemplate: ShiftTemplate
  selectedStart?: Date
  selectedEnd?: Date
  isDisabled?: boolean
  eligibleStaff?: EligibleStaffResponse[]
  onStaffSelect?: (staffId: string) => void
  isAdmin?: boolean
}

export function ShiftTemplateDialog({
  open,
  onClose,
  onConfirm,
  onDeleteClick,
  onEditClick,
  shiftTemplate,
  selectedStart,
  selectedEnd,
  isDisabled = false,
  eligibleStaff = [],
  onStaffSelect,
  isAdmin = false,
}: ShiftTemplateDialogProps) {
  useLingui()
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([])
  const isMobile = useIsMobile()

  const requiredStaffCount = shiftTemplate.requiredStaffCount || 1

  const handleStaffSelected = (staffIds: string[]) => {
    setSelectedStaffIds(staffIds)
    if (staffIds.length === 1 && onStaffSelect) {
      onStaffSelect(staffIds[0])
    }
  }

  const handleConfirm = () => {
    onConfirm(selectedStaffIds)
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle display="flex" justifyContent="space-between">
        <Trans>Shift Details</Trans>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={onEditClick} size="small" color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onDeleteClick} size="small" color="primary">
            <DeleteIcon color="error" fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              <Trans>Type of location</Trans>
            </Typography>
            <Typography>{shiftTemplate.location}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              <Trans>Consultation reason</Trans>
            </Typography>
            <Typography>{shiftTemplate.consultationReason}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              <Trans>Staff required</Trans>
            </Typography>
            <Typography>{requiredStaffCount}</Typography>
          </Box>

          {selectedStart && selectedEnd && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                <Trans>Selected time</Trans>
              </Typography>
              <Typography>
                {dayjs(selectedStart).format('DD/MM/YYYY HH:mm')} - {dayjs(selectedEnd).format('HH:mm')}
              </Typography>
            </Box>
          )}

          {isAdmin && eligibleStaff.length > 0 && (
            <>
              <Divider />
              <StaffSelector
                eligibleStaffs={eligibleStaff}
                isMultipleStaffRequired={requiredStaffCount > 1}
                onStaffSelected={handleStaffSelected}
                requiredStaffCount={requiredStaffCount}
                selectedStaffIds={selectedStaffIds}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <Button onClick={onClose} color="inherit">
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={isDisabled || selectedStaffIds.length === 0}>
          <Trans>Confirm</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
